import * as React from "react";
import { ForwardedRef, forwardRef, useCallback, useState } from "react";
import styled from "@mui/material/styles/styled";
import { Button } from "@mui/material";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { CHAT_ELEMENT_HEIGHT } from "../consts/sizeConsts";
import { PatientColorMap } from "../../layout/patientColorMap";
import { useWidgetSettings } from "../../widget/useWidgetSettings";
import clsx from "clsx";
import { useClinicData } from "../../clinic/clinicHooks";

//region [[ Styles ]]

const GenericButtonView = styled(Button)(({ theme }) => ({
  "&.generic-button, &.generic-button:hover": {
    color: PatientColorMap.ButtonTextColor,
    backgroundColor: PatientColorMap.Primary,
    height: CHAT_ELEMENT_HEIGHT,
    fontWeight: 500,
    fontSize: "15px",
    textTransform: "none",
    minWidth: "120px",
    borderRadius: "30px",
    boxShadow: "unset",
    "&:focus, &:hover, &:active": {
      color: PatientColorMap.ButtonTextColor,
      boxShadow: "unset",
      backgroundColor: PatientColorMap.Primary,
    },
    "&.MuiButton-root.disabled, &.MuiButton-root.disabled": {
      color: PatientColorMap.DisabledText,
      boxShadow: "unset",
      backgroundColor: PatientColorMap.Disabled,
    },
    "& svg": {
      margin: "0 5px",
    },
    "&.disabled.clicked, &.disabled.clicked:focus, &.disabled.clicked:hover, &.disabled.clicked:active, &.disabled.clicked, &.patient-Mui-disabled.clicked:focus, &.patient-Mui-disabled.clicked:hover, &.patient-Mui-disabled.clicked:active": {
      color: PatientColorMap.AfterClickText,
      boxShadow: "unset",
      backgroundColor: PatientColorMap.AfterClick,
    },
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface GenericButtonProps {
  onClick: (event) => void;
  label: string;
  disabled?: boolean;
  className?: string;
  /***
   * Whether we want to skip reporting on this butting being pushed
   */
  skipAnalytics?: boolean;
  selected?: boolean;
  icon?: React.ReactNode;
  allowReclicking?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const GenericButton = forwardRef(
  (
    { label, onClick, skipAnalytics, ...props }: GenericButtonProps,
    ref: ForwardedRef<any>,
  ) => {
    const { partnerId } = useWidgetSettings();

    const { name: clinicName, clinic: clinicId } = useClinicData();

    const [clicked, setClicked] = useState(false);

    const reportButtonClick = useCallback(
      (e) => {
        if (!skipAnalytics) {
          evt({
            category: CATEGORY.PATIENT_GENERAL,
            action: ACTION.PATIENT_BUTTON_CLICKED,
            label: label,
            partnerId,
            props: {
              clinicName,
              clinicId,
            },
          });
        }
        onClick?.(e);
        setClicked(true);
      },
      [label, onClick, skipAnalytics, partnerId, clinicId, clinicName],
    );

    const disabled = (!props.allowReclicking && clicked) || props.disabled;

    return (
      <GenericButtonView
        ref={ref}
        variant={"contained"}
        className={clsx(
          props.className,
          clicked ? "clicked" : "",
          disabled ? "disabled" : "",
          "generic-button",
        )}
        onClick={reportButtonClick}
        disabled={disabled}
      >
        {props.icon}
        {label}
      </GenericButtonView>
    );
  },
);
